import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssessmentStage, AssessmentStatus, Grade, ReviewChain } from '@cfl/shared';

import { api, type ApiError } from '@/lib/api';
import type { GoalUpdate } from '@/lib/goals-api';

export interface AssessmentKra {
  kraId: string;
  goalId: string;
  goalTitle: string;
  goalDescription: string | null;
  goalWeightage: number;
  name: string;
  description: string | null;
  /** This KRA's share of its goal. All-zero under a goal means they count equally. */
  weightage: number;
  /**
   * One entry per stage that has touched this KRA. `id` keys that stage's evidence.
   *
   * `rating: null` is a row logged during the quarter — a comment and its evidence with
   * no band picked yet. It is not a rating of zero and must never render as one.
   */
  ratings: Array<{
    id: string;
    stage: AssessmentStage;
    rating: number | null;
    comment: string | null;
  }>;
}

export interface Assessment {
  id: string;
  employeeId: string;
  employeeName: string;
  quarterId: string;
  quarterLabel: string;
  status: AssessmentStatus;
  stageAwaiting: AssessmentStage | null;
  completedStages: AssessmentStage[];
  /** The stages this quarter runs. HR chose it when the quarter was opened. */
  chain: ReviewChain;
  achievements: string | null;
  challenges: string | null;
  submittedAt: string | null;
  lockedAt: string | null;
  managerDueAt: string | null;
  managerOverdue: boolean;
  returnedAt: string | null;
  returnNote: string | null;
  /**
   * Why the employee declined the manager's rating — the manager's brief for what to
   * change. Non-null only while it is back at the manager stage after a decline.
   *
   * Not `returnNote`: that one means "a reviewer sent this back to the employee", and
   * the Returned pill hangs off it.
   */
  ratingDeclinedNote: string | null;
  reopenedUntil: string | null;
  reopenActive: boolean;
  /** Window shut with nothing submitted. Derived by the API, never a stored status. */
  overdue: boolean;
  finalRating: number | null;
  finalGrade: Grade | null;
  /** The 9-box y axis. Null means unscored, and the grid falls back to the trend. */
  potentialRating: number | null;
  promotionRecommended: boolean;
  bonusRecommended: boolean;
  kras: AssessmentKra[];
  /** Progress logged on My goals, per organization goal, newest first. Read-only here. */
  goalUpdates: Array<GoalUpdate & { goalId: string }>;
  stages: Array<{
    stage: AssessmentStage;
    actorName: string;
    overallRating: number | null;
    feedback: string | null;
    comments: string | null;
    submittedAt: string;
  }>;
  windowOpen: boolean;
  frozen: boolean;
  canEditSelf: boolean;
  canReturn: boolean;
  canReopen: boolean;
  myPendingStage: AssessmentStage | null;
}

const key = (employeeId: string, quarterId: string) =>
  ['assessment', employeeId, quarterId] as const;

export function useAssessment(employeeId: string | 'me', quarterId: string | undefined) {
  return useQuery<Assessment, ApiError>({
    queryKey: key(employeeId, quarterId ?? ''),
    queryFn: () =>
      api.get<Assessment>(
        employeeId === 'me'
          ? `/assessments/me?quarterId=${quarterId}`
          : `/assessments/employee/${employeeId}?quarterId=${quarterId}`,
      ),
    enabled: Boolean(quarterId),
  });
}

/** Everything waiting on the signed-in user. Also drives the nav count badges. */
export function usePendingAssessments() {
  return useQuery<Assessment[], ApiError>({
    queryKey: ['assessments', 'pending'],
    queryFn: () => api.get<Assessment[]>('/assessments/pending'),
    staleTime: 60_000,
  });
}

/**
 * The reviews waiting on this person at one stage of the chain.
 *
 * The ONE definition, read by the sidebar badge and by the reviews page it links to.
 * They used to disagree: the badge counted `myPendingStage`, the page listed everything
 * across every stage — so "HR reviews · 3" opened a list of eight, and the pill promised
 * a filter the page did not apply. A number on a link has to be the number of rows
 * behind it, or it stops being read at all.
 */
export function pendingAtStage(
  pending: Assessment[] | undefined,
  stage: AssessmentStage,
): Assessment[] {
  return (pending ?? []).filter((a) => a.myPendingStage === stage);
}

/**
 * Whether the signed-in user's OWN self-assessment is open right now. pending only
 * covers reviews they owe others, so without this the dashboard and the sidebar
 * badge had no signal for the one action that is theirs alone.
 */
export type SelfAssessmentSummary =
  | { exists: false; actionable: false }
  | {
      exists: true;
      quarterId: string;
      quarterLabel: string;
      status: AssessmentStatus;
      windowOpen: boolean;
      windowReason:
        | 'FROZEN'
        | 'FORCED_OPEN'
        | 'FORCED_CLOSED'
        | 'OPEN_ON_CALENDAR'
        | 'BEFORE_WINDOW'
        | 'AFTER_WINDOW';
      opensInDays: number | null;
      closesInDays: number | null;
      goalsPublished: boolean;
      selfNotesLogged: boolean;
      chain: ReviewChain;
      /** Window open, not yet submitted, goals live — they can fill it in right now. */
      actionable: boolean;
      /** Submitted and rated, and now waiting on this employee to accept or decline. */
      awaitingMyAcceptance: boolean;
    };

export function useMyCurrentSelf() {
  return useQuery<SelfAssessmentSummary, ApiError>({
    queryKey: ['assessments', 'me', 'current'],
    queryFn: () => api.get<SelfAssessmentSummary>('/assessments/me/current'),
    staleTime: 60_000,
  });
}

export interface KraRatingInput {
  kraId: string;
  /**
   * Null while the quarter is running: a KRA can carry a comment and its evidence with
   * no band chosen. Submission is where the API insists on one.
   */
  rating: number | null;
  comment?: string | null;
}

function useAssessmentMutation<TArgs>(
  employeeId: string | 'me',
  quarterId: string,
  fn: (args: TArgs) => Promise<Assessment>,
) {
  const queryClient = useQueryClient();
  return useMutation<Assessment, ApiError, TArgs>({
    mutationFn: fn,
    onSuccess: (next) => {
      queryClient.setQueryData(key(employeeId, quarterId), next);
      // The review queue and its badge change the moment a stage advances.
      void queryClient.invalidateQueries({ queryKey: ['assessments', 'pending'] });
    },
  });
}

export function useSaveSelf(employeeId: string | 'me', quarterId: string, assessmentId: string) {
  return useAssessmentMutation<{
    achievements?: string | null;
    challenges?: string | null;
    ratings: KraRatingInput[];
  }>(employeeId, quarterId, (body) =>
    api.post<Assessment>(`/assessments/${assessmentId}/self`, body),
  );
}

/**
 * A reviewer's autosave — their KRA rows, nothing advanced.
 *
 * The mirror of `useSaveSelf` for whoever holds a review stage. Evidence attaches to
 * the KraRating row, so without this a manager had nothing to attach to until after
 * they submitted — by which point their stage is closed.
 */
export function useSaveReviewDraft(
  employeeId: string | 'me',
  quarterId: string,
  assessmentId: string,
) {
  return useAssessmentMutation<{ ratings: KraRatingInput[] }>(employeeId, quarterId, (body) =>
    api.post<Assessment>(`/assessments/${assessmentId}/review-draft`, body),
  );
}

export function useSubmitStage(employeeId: string | 'me', quarterId: string, assessmentId: string) {
  return useAssessmentMutation<{
    stage: AssessmentStage;
    ratings?: KraRatingInput[];
    feedback?: string | null;
    comments?: string | null;
    overallRating?: number | null;
    potentialRating?: number | null;
    promotionRecommended?: boolean;
    bonusRecommended?: boolean;
  }>(employeeId, quarterId, (body) =>
    api.post<Assessment>(`/assessments/${assessmentId}/stage`, body),
  );
}

/**
 * The employee's answer to their manager's rating.
 *
 * Accepting settles it for the quarter and moves it to HR; declining sends it back to
 * the manager to resubmit, so the reason is required there and optional here.
 */
export function useAcceptRating(
  employeeId: string | 'me',
  quarterId: string,
  assessmentId: string,
) {
  return useAssessmentMutation<{ note?: string | null }>(employeeId, quarterId, (body) =>
    api.post<Assessment>(`/assessments/${assessmentId}/accept-rating`, body),
  );
}

export function useDeclineRating(
  employeeId: string | 'me',
  quarterId: string,
  assessmentId: string,
) {
  return useAssessmentMutation<{ note: string }>(employeeId, quarterId, (body) =>
    api.post<Assessment>(`/assessments/${assessmentId}/decline-rating`, body),
  );
}

/** Send it back to the employee. The note is required - see the API's returnSchema. */
export function useReturnForCorrection(
  employeeId: string | 'me',
  quarterId: string,
  assessmentId: string,
) {
  return useAssessmentMutation<{ note: string }>(employeeId, quarterId, (body) =>
    api.post<Assessment>(`/assessments/${assessmentId}/return`, body),
  );
}

/** HR reopens a locked assessment. `until` is an ISO instant, not a business date. */
export function useReopenAssessment(
  employeeId: string | 'me',
  quarterId: string,
  assessmentId: string,
) {
  return useAssessmentMutation<{ until: string; reason: string }>(
    employeeId,
    quarterId,
    (body) => api.post<Assessment>(`/assessments/${assessmentId}/reopen`, body),
  );
}
